/**
 * Helper functions for the PropBrowserModal.
 * - propToBuilderLeg: Convert a BettableProp to a BuilderLeg
 * - normalizeOU: Normalize over/under for comparison
 * - matchParsedLegs: Match parsed import text to available props
 * - matchTeamBet: Internal helper for team-level bet matching
 */

import type { BuilderLeg } from '@parlay-picker/shared/builderLeg';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { isGameTotalMarket } from '@parlay-picker/shared/marketKey';
import { fuzzyMatchPlayer } from '@parlay-picker/shared/playerNameUtils';
import { getTeamSide } from '@parlay-picker/shared/teams';
import type { BettableProp, Game, ParsedLeg } from '@utils/propUtils';
import { formatPropType, propTypeToStatType } from '@utils/propUtils';

import type { MatchResult } from './propBrowserTypes';

/**
 * Convert a BettableProp to a BuilderLeg
 * @param prop - The prop to convert
 * @param sportsbook - The sportsbook for this prop
 * @param games - Available games (used to construct matchup format for totals)
 */
export function propToBuilderLeg(
  prop: BettableProp,
  sportsbook: string,
  games: Game[],
  leagueKey: LeagueKey,
): BuilderLeg {
  const game = prop.gameId ? games.find((g) => g.id === prop.gameId) : undefined;
  const awayTeam = game?.away_team;
  const homeTeam = game?.home_team;

  // For totals, construct matchup format "Away @ Home" so BuilderLegRow can display both logos
  let teamValue = prop.team || '';
  let side: 'home' | 'away' | null = null;
  if (awayTeam && homeTeam) {
    if (isGameTotalMarket(prop.propType)) {
      teamValue = `${awayTeam} @ ${homeTeam}`;
    } else {
      side = getTeamSide(prop.team, { home_team: homeTeam, away_team: awayTeam }, leagueKey);
    }
  }

  return {
    id: `prop-${prop.id}-${Date.now()}`,
    player: prop.playerName ?? null,
    playerId: null, // BettableProp doesn't have playerId
    team: teamValue,
    opponentTeam:
      side === 'home' ? (awayTeam ?? null) : side === 'away' ? (homeTeam ?? null) : null,
    isHomeGame: side === null ? null : side === 'home',
    statType: propTypeToStatType(prop.propType),
    line: typeof prop.line === 'number' ? prop.line : null,
    overUnder: (prop.overUnder as 'Over' | 'Under') ?? null,
    odds: prop.odds || '',
    gameId: prop.gameId ?? null,
    gameDate: prop.gameDate ?? null, // From game's commence_time
    gameStartsAt: game?.commence_time ?? prop.gameDate ?? null,
    sportsbook,
    marketKey: prop.propType ?? null,
    // No AI context for manual props
    aiAnalysis: null,
    aiConfidence: null,
    // No situational context from basic props
    isB2b: null,
    blowoutRisk: null,
    matchupNote: null,
    recentForm: null,
    injuryContext: null,
    // Source
    source: 'manual',
    provenance: 'manual',
    // League tracking
    leagueKey,
  };
}

/**
 * Normalize over/under for comparison (handles o/O/over/Over/OVER etc.)
 */
export function normalizeOU(value: string | undefined): string {
  if (!value) return '';
  const lower = value.toLowerCase();
  if (lower === 'o' || lower === 'over') return 'over';
  if (lower === 'u' || lower === 'under') return 'under';
  return lower;
}

/**
 * Match parsed legs to available props
 */
export function matchParsedLegs(
  parsed: ParsedLeg[],
  props: BettableProp[],
  games: Game[],
): MatchResult[] {
  // Determine which games have props available
  const gameIdsWithProps = new Set(props.map((p) => p.gameId).filter(Boolean));
  const gamesWithoutProps = games.filter((g) => !gameIdsWithProps.has(g.id));

  return parsed.map((leg) => {
    // Handle team bets differently
    if (leg.isTeamBet) {
      return matchTeamBet(leg, props, games);
    }

    const legStatType = propTypeToStatType(leg.propType);

    // Filter props by player name (fuzzy match with nickname support)
    const playerMatches = props.filter((p) => {
      if (!p.playerName) return false;
      return fuzzyMatchPlayer(leg.playerName, p.playerName);
    });

    if (playerMatches.length === 0) {
      // Check if there are games without props - player might be from one of those
      if (gamesWithoutProps.length > 0) {
        const gamesList = gamesWithoutProps
          .map((g) => `${g.away_team} @ ${g.home_team}`)
          .join(', ');
        return {
          parsed: leg,
          matched: null,
          reason: `Player "${leg.playerName}" not found. Props not yet available for: ${gamesList}`,
        };
      }
      return {
        parsed: leg,
        matched: null,
        reason: `Player "${leg.playerName}" not found in available props`,
      };
    }

    // Filter by prop type
    const typeMatches = playerMatches.filter((p) => {
      const propStatType = propTypeToStatType(p.propType);
      return propStatType === legStatType;
    });

    if (typeMatches.length === 0) {
      // List what prop types ARE available for this player
      const availableTypes = [
        ...new Set(playerMatches.map((p) => formatPropType(p.propType))),
      ].slice(0, 3);
      return {
        parsed: leg,
        matched: null,
        reason: `No ${formatPropType(leg.propType)} props for ${leg.playerName}. Available: ${availableTypes.join(', ')}`,
      };
    }

    // Filter by over/under (normalized to handle o/over/O/Over etc.)
    const ouMatches = typeMatches.filter(
      (p) => normalizeOU(p.overUnder) === normalizeOU(leg.overUnder),
    );

    if (ouMatches.length === 0) {
      // No matches for this direction - include all type matches as alternatives
      return {
        parsed: leg,
        matched: null,
        reason: `No ${leg.overUnder} lines available`,
        alternatives: typeMatches, // Let user pick from opposite direction
      };
    }

    // First try to find EXACT line match
    const exactMatch = ouMatches.find((p) => p.line === leg.line);
    if (exactMatch) {
      // Include other options as alternatives for user to modify if desired
      return {
        parsed: leg,
        matched: exactMatch,
        alternatives: typeMatches.filter((p) => p.id !== exactMatch.id),
      };
    }

    // If user entered a whole number (e.g., "4"), try line - 0.5 (e.g., 3.5)
    // Odds API uses half-points, so "Over 4" typically means "Over 3.5" in their system
    const isWholeNumber = leg.line === Math.floor(leg.line);
    if (isWholeNumber) {
      const adjustedLine = leg.line - 0.5;
      const adjustedMatch = ouMatches.find((p) => p.line === adjustedLine);
      if (adjustedMatch) {
        return {
          parsed: leg,
          matched: adjustedMatch,
          reason: `Adjusted ${leg.line} → ${adjustedLine} (Odds API uses half-points)`,
          alternatives: typeMatches.filter((p) => p.id !== adjustedMatch.id),
        };
      }
    }

    // No exact match - find closest and warn
    let best = ouMatches[0];
    let bestDiff = Math.abs((best.line ?? 0) - leg.line);
    for (const prop of ouMatches) {
      const diff = Math.abs((prop.line ?? 0) - leg.line);
      if (diff < bestDiff) {
        best = prop;
        bestDiff = diff;
      }
    }

    // Include all type matches as alternatives so user can pick different line/direction
    return {
      parsed: leg,
      matched: best,
      reason: `Line ${leg.line} not available`,
      alternatives: typeMatches.filter((p) => p.id !== best.id),
    };
  });
}

/**
 * Match team bets (spreads, totals, moneyline)
 */
export function matchTeamBet(leg: ParsedLeg, props: BettableProp[], games: Game[]): MatchResult {
  const teamName = (leg.teamName || leg.playerName).toLowerCase();
  const propType = leg.propType.toLowerCase();

  // Filter by prop type first
  const typeMatches = props.filter((p) => p.propType?.toLowerCase() === propType);

  if (typeMatches.length === 0) {
    return {
      parsed: leg,
      matched: null,
      reason: `No ${formatPropType(propType)} props available`,
    };
  }

  // For totals, match on over/under, line, and optionally game
  if (propType === 'totals') {
    let ouMatches = typeMatches.filter(
      (p) => normalizeOU(p.overUnder) === normalizeOU(leg.overUnder),
    );

    if (ouMatches.length === 0) {
      return {
        parsed: leg,
        matched: null,
        reason: `No ${leg.overUnder} totals available`,
      };
    }

    // If teams were specified (e.g., "Lakers Over 220.5" or "Lakers vs Celtics Over 220.5"), filter by game
    if (teamName && teamName !== 'game') {
      // Extract team names from the input (split by "vs", "@", "at", etc.)
      // Whitespace around separator is optional to handle "lakers@celtics" and "lakers @ celtics"
      const teamParts = teamName
        .split(/\s*(?:vs\.?|@|at)\s*/i)
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);

      if (teamParts.length > 0 && teamParts[0]) {
        // Find games that involve any of the mentioned teams
        const matchingGameIds = new Set<string>();
        for (const game of games) {
          const homeTeam = (game.home_team || '').toLowerCase();
          const awayTeam = (game.away_team || '').toLowerCase();
          // Check if any team part matches home or away team
          const matches = teamParts.some(
            (part) =>
              homeTeam.includes(part) ||
              part.includes(homeTeam) ||
              awayTeam.includes(part) ||
              part.includes(awayTeam),
          );
          if (matches) {
            matchingGameIds.add(game.id);
          }
        }

        if (matchingGameIds.size > 0) {
          // Filter props to only those from matching games
          const gameMatches = ouMatches.filter((p) => p.gameId && matchingGameIds.has(p.gameId));
          if (gameMatches.length > 0) {
            ouMatches = gameMatches;
          }
        }
        // If no game matches found, fall through to use all ouMatches (best effort)
      }
    }

    // Check for ambiguity - multiple unique gameIds
    const uniqueGameIds = new Set(ouMatches.map((p) => p.gameId).filter(Boolean));
    const isAmbiguous = uniqueGameIds.size > 1 && (!teamName || teamName === 'game');

    // Find closest line
    let best = ouMatches[0];
    let bestDiff = Math.abs((best.line ?? 0) - leg.line);
    for (const prop of ouMatches) {
      const diff = Math.abs((prop.line ?? 0) - leg.line);
      if (diff < bestDiff) {
        best = prop;
        bestDiff = diff;
      }
    }

    // Build reason message
    let reason: string | undefined;
    if (isAmbiguous) {
      reason = `Multiple games available - specify team (e.g., "Lakers Over ${leg.line}")`;
    } else if (bestDiff > 0) {
      reason = `Line adjusted: ${leg.line} → ${best.line}`;
    }

    return { parsed: leg, matched: best, reason };
  }

  // For spreads and h2h, match by team name
  const teamMatches = typeMatches.filter((p) => {
    const propTeam = (p.team || p.playerName || '').toLowerCase();
    return propTeam.includes(teamName) || teamName.includes(propTeam);
  });

  if (teamMatches.length === 0) {
    return {
      parsed: leg,
      matched: null,
      reason: `No ${formatPropType(propType)} for ${leg.teamName || leg.playerName}`,
    };
  }

  // For spreads, find closest line
  if (propType === 'spreads') {
    let best = teamMatches[0];
    let bestDiff = Math.abs((best.line ?? 0) - leg.line);
    for (const prop of teamMatches) {
      const diff = Math.abs((prop.line ?? 0) - leg.line);
      if (diff < bestDiff) {
        best = prop;
        bestDiff = diff;
      }
    }
    if (bestDiff > 0) {
      return {
        parsed: leg,
        matched: best,
        reason: `Line adjusted: ${leg.line} → ${best.line}`,
      };
    }
    return { parsed: leg, matched: best };
  }

  // For h2h (moneyline), just return first match
  return { parsed: leg, matched: teamMatches[0] };
}
